import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Megaphone, Target, BarChart3 } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { useCurrency } from "@/context/CurrencyContext";
import { Reveal, SectionHead } from "@/components/site/Reveal";
import Icon from "@/components/site/Icon";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import QuoteCalculator from "@/components/site/QuoteCalculator";

export default function Advertising() {
  useSeo({ title: "Advertising & Digital Marketing", description: "Worldwide paid advertising across Google, Meta, YouTube, LinkedIn & TikTok with transparent fixed pricing and a live quote calculator." });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const { config, fmt } = useCurrency() || {};
  const adServices = services.filter((s) => s.category === "Advertising" && s.slug !== "advertising");
  const packages = config?.packages || [];

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border">
        <div className="bs-aurora pointer-events-none absolute inset-0 opacity-70" />
        <div className="bs-grid-bg pointer-events-none absolute inset-0" />
        <div className="bs-container relative py-16 lg:py-24">
          <Reveal>
            <span className="bs-eyebrow"><Megaphone className="h-3.5 w-3.5 text-primary" /> Worldwide Advertising</span>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-black sm:text-5xl lg:text-6xl">
              Ads that reach the right people, <span className="bs-gradient-text">everywhere</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
              Transparent fixed pricing, multi-currency quotes and expert campaign management across Google, Meta, YouTube, LinkedIn and TikTok. Your management fee is always separate from your advertising spend.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full shadow-glow"><a href="#calculator">Start Your Campaign <ArrowRight className="ml-2 h-4 w-4" /></a></Button>
              <Button asChild size="lg" variant="outline" className="rounded-full"><Link to="/consultation">Book Consultation</Link></Button>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bs-container py-16 lg:py-20">
        <Reveal><SectionHead center eyebrow="Ad services" title="Every channel that matters" subtitle="Pick a channel or let us build a multi-platform strategy." /></Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {adServices.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.03}>
              <Link to={`/services/${s.slug}`} data-testid={`ad-service-${s.slug}`}
                className="group flex h-full flex-col bs-card p-6 hover:-translate-y-1 hover:border-primary/50 hover:shadow-glow">
                <div className="mb-4 inline-flex w-fit rounded-xl bg-primary/12 p-3 text-primary transition-transform group-hover:scale-110"><Icon name={s.icon} /></div>
                <h3 className="font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.short_desc}</p>
                <span className="mt-4 inline-flex items-center text-sm font-semibold text-primary">Learn more <ArrowRight className="ml-1 h-4 w-4" /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {packages.length > 0 && (
        <section className="border-y border-border bg-card/30 py-16 lg:py-20">
          <div className="bs-container">
            <Reveal><SectionHead center eyebrow="Fixed packages" title="Transparent monthly management fees"
              subtitle="Clear, editable pricing. Advertising platform spend is always separate and controlled by you." /></Reveal>
            <div className="mt-12 grid gap-6 lg:grid-cols-4">
              {packages.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.05}>
                  <div className={`flex h-full flex-col bs-card p-6 ${p.popular ? "border-primary shadow-glow" : ""}`} data-testid={`ad-package-${p.id}`}>
                    {p.popular && <Badge className="mb-2 w-fit">Most popular</Badge>}
                    <h3 className="font-display text-lg font-bold">{p.name}</h3>
                    <p className="text-sm text-muted-foreground">{p.tagline}</p>
                    <p className="mt-4 font-display text-3xl font-black">{Object.keys(p.prices || {}).length ? <>{fmt?.(p.prices)}<span className="text-sm font-medium text-muted-foreground">/{p.period}</span></> : "Custom"}</p>
                    <ul className="mt-5 flex-1 space-y-2.5">
                      {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" /> {f}</li>)}
                    </ul>
                    <Button asChild className="mt-6 w-full rounded-full" variant={p.popular ? "default" : "outline"}><a href="#calculator">Choose Package</a></Button>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-6 text-center text-xs text-muted-foreground">Prices marked * are shown in your default currency. No leads, sales or ROI are guaranteed.</p>
          </div>
        </section>
      )}

      <section id="calculator" className="bs-container py-16 lg:py-24 scroll-mt-20">
        <Reveal><SectionHead center eyebrow="Smart quote calculator" title="Know your exact cost in seconds"
          subtitle="Select a package, platform, budget and add-ons to generate a professional proposal instantly." /></Reveal>
        <div className="mt-10"><QuoteCalculator /></div>
      </section>

      <section className="border-t border-border bg-card/30 py-16">
        <div className="bs-container grid gap-6 sm:grid-cols-3">
          {[[Target, "Transparent pricing", "Fixed fees, clearly separated from ad spend."],
            [BarChart3, "Real reporting", "Monthly reports on the metrics that matter."],
            [Megaphone, "Expert management", "Hands-on optimization across every channel."]].map(([Ic, t, d]) => (
            <div key={t} className="bs-card p-6"><Ic className="h-6 w-6 text-primary" /><h3 className="mt-3 font-display font-bold">{t}</h3><p className="mt-1 text-sm text-muted-foreground">{d}</p></div>
          ))}
        </div>
      </section>
    </div>
  );
}
